// @flow
import RootNodeProvider from "./RootNodeProvider";
import Configuration from "../Configuration";
import {readRouteFromSearch} from "./queryHistory";

/**
 * Router mode of this embed, resolved once at boot (the history singleton is
 * created at import time, long before the dealer configuration arrives):
 *
 *   1. `data-router="hash|query|browser"` on the mount element
 *   2. `data-seo-deeplinks` on the mount element (legacy alias for browser)
 *   3. runtime list REACT_APP_HPM_QUERY_ROUTING: `none` | comma-separated
 *      dealer/garage/group ids | `all` → query
 *   4. hash
 */

export const ROUTER_MODES = Object.freeze({HASH: "hash", QUERY: "query", BROWSER: "browser"});
export const QUERY_PARAM_NAME = "shop";

const MODES: string[] = Object.keys(ROUTER_MODES).map(key => ROUTER_MODES[key]);

export const matchesQueryRoutingList = (config: ?string, ids: Array<?string>): boolean => {
    const value = (config || "").trim().toLowerCase();
    if (!value || value === "none") {
        return false;
    }
    if (value === "all") {
        return true;
    }
    const list = value.split(",").map(entry => entry.trim()).filter(Boolean);
    return ids.some(id => !!id && list.includes(String(id).trim().toLowerCase()));
};

// the hosted variant (src/preview) mounts the module itself and knows mode and basename from
// its own URL before the module's history exists — it announces them on the window
export type HostedRouterOverride = {mode: string, basename?: ?string, hosted?: boolean};

const hostedOverride = (): ?HostedRouterOverride =>
    (typeof window !== "undefined" && window.__HPM_ROUTER__) ? window.__HPM_ROUTER__ : null;

export const resolveRouterMode = (
    rootNode: ?HTMLElement,
    queryRoutingConfig: ?string,
    override: ?HostedRouterOverride = hostedOverride()
): string => {
    if (override && MODES.includes(override.mode)) {
        return override.mode;
    }
    if (!rootNode || !rootNode.dataset) {
        // nothing is mounted — there is nothing to route
        return ROUTER_MODES.HASH;
    }
    const dataset = rootNode.dataset;
    const explicit = (dataset.router || "").trim().toLowerCase();
    if (MODES.includes(explicit)) {
        return explicit;
    }
    if ("seoDeeplinks" in dataset) {
        return ROUTER_MODES.BROWSER;
    }
    if (matchesQueryRoutingList(queryRoutingConfig, [dataset.dealerId, dataset.garageId, dataset.garageGroupId])) {
        return ROUTER_MODES.QUERY;
    }
    return ROUTER_MODES.HASH;
};

export const normalizeBasename = (basePath: ?string): string => {
    const trimmed = (basePath || "").trim();
    if (!trimmed || trimmed === "/") {
        return "";
    }
    return `/${trimmed.replace(/^\/+|\/+$/g, "")}`;
};

let cachedMode: ?string = null;

export const getRouterMode = (): string => {
    if (cachedMode == null) {
        cachedMode = resolveRouterMode(RootNodeProvider.getRootNode(), Configuration.value("hpmQueryRouting"));
    }
    return cachedMode;
};

// the mount element can change between tests only
export const resetRouterModeCache = () => {
    cachedMode = null;
};

// the path the module is mounted under in browser mode: announced by the hosted variant, or
// `data-base-path` on the mount element
export const getBasename = (): string => {
    const override = hostedOverride();
    if (override && override.basename) {
        return normalizeBasename(override.basename);
    }
    const root = RootNodeProvider.getRootNode();
    return normalizeBasename(root && root.dataset ? root.dataset.basePath : "");
};

export const isHostedVariant = (): boolean => {
    const override = hostedOverride();
    return !!(override && override.hosted);
};

export const isIndexableRouterMode = (mode: string = getRouterMode()): boolean => mode !== ROUTER_MODES.HASH;

// the route as the module router sees it, independent of the mode
export const currentRouterPath = (): string => {
    const {pathname, hash, search} = window.location;
    switch (getRouterMode()) {
        case ROUTER_MODES.QUERY:
            return readRouteFromSearch(search, QUERY_PARAM_NAME) || "/";
        case ROUTER_MODES.BROWSER: {
            const basename = getBasename();
            return basename && pathname.startsWith(basename) ? (pathname.slice(basename.length) || "/") : pathname;
        }
        default:
            return hash.startsWith("#/") ? hash.slice(1) : pathname;
    }
};

const BACKEND_ROUTING_TO_MODE = {HASH: ROUTER_MODES.HASH, QUERY: ROUTER_MODES.QUERY, PATH: ROUTER_MODES.BROWSER};

// the backend flag drives feed/sitemap URLs — a mismatch means those URLs
// will not match what this embed produces
export const warnOnRoutingMismatch = (backendRouting: ?string) => {
    if (!backendRouting) {
        return;
    }
    const expected = BACKEND_ROUTING_TO_MODE[String(backendRouting).toUpperCase()];
    if (expected && expected !== getRouterMode()) {
        // eslint-disable-next-line no-console
        console.warn(`HPM: router mode "${getRouterMode()}" differs from the configured shopRouting "${backendRouting}" — feed and sitemap URLs will not match this embed.`);
    }
};
